import React from 'react';
import { ControlLink, Text, Stack } from '@moda/om';
import { formatPrice } from '@moda/portal-stanchions';
import { hrefFor } from '../../../routers';
import { Order, OrderItem } from '../../../hooks/useOrder';
import { AccountOrderBodyActions } from '../../AccountOrderHistory/AccountOrder/AccountOrderBody/AccountOrderBodyActions';
import { AccountOrderErrorMessage } from '../../AccountOrderHistory/AccountOrderErrorMessage';

import '../AccountOrderHeader.scss';

interface Props {
  order: Order;
  categoryName: string;
  showShipping: boolean;
  showOrderTotal: boolean;
  showRemainingBalance: boolean;
  showPayRemainingBalance: boolean;
  orderItems?: OrderItem[] | undefined;
}

export const AccountOrderHeaderMobile: React.FC<Props> = ({
  order,
  categoryName,
  showShipping,
  showOrderTotal,
  showRemainingBalance,
  orderItems
}) => {
  const hasRemainingBalance: boolean = Number(order.attributes.totals.remainingBalance.price) > 0;

  const orderDate = new Date(order.attributes.createdAt).toLocaleDateString('us-EN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  return (
    <div className="AccountOrderHeader__header-mobile">
      <Stack space={4}>
        <Stack space={8} direction="horizontal">
          <Stack space={1} className="AccountOrderHeader__number">
            <Text treatment="body2" color="cement">
              Order Number
            </Text>
            <ControlLink
              to={hrefFor.OrderDetailPage({ orderNumber: order.attributes.orderNumber })}
              className="AccountOrderHeader__link"
            >
              #{order.attributes.orderNumber}
            </ControlLink>
          </Stack>
          <Stack space={1} className="AccountOrderHeader__date">
            <Text treatment="body2" color="cement">
              Order Date
            </Text>
            <Text treatment="body2" color="ink">
              {orderDate}
            </Text>
          </Stack>
        </Stack>

        <Stack space={8} direction="horizontal">
          {showShipping && (
            <Stack space={1} className="AccountOrderHeader__shipping">
              <Text treatment="body2" color="cement">
                Ship to
              </Text>
              <Text treatment="body2" color="ink">
                {order.attributes.address.addressLine1} {order.attributes.address.addressLine2},{' '}
                {order.attributes.address.city}, {order.attributes.address.state},{' '}
                {order.attributes.address.postalCode}, {order.attributes.address.countryCode}{' '}
              </Text>
            </Stack>
          )}
          {showOrderTotal && (
            <Stack space={1} className="AccountOrderHeader__total">
              <Text treatment="body2" color="cement">
                Order Total
              </Text>
              <Text treatment="body2" color="ink">
                {formatPrice(
                  order.attributes.totals.orderTotal.currency,
                  order.attributes.totals.orderTotal.price
                )}
              </Text>
            </Stack>
          )}
          {showRemainingBalance && hasRemainingBalance && (
            <Stack space={1} className="AccountOrderHeader__remain-balance">
              <Text treatment="body2" color="cement">
                Remaining Balance
              </Text>
              <Text treatment="body2" color="ink">
                {formatPrice(
                  order.attributes.totals.remainingBalance.currency,
                  order.attributes.totals.remainingBalance.price
                )}
              </Text>
            </Stack>
          )}
        </Stack>

        {orderItems && orderItems.length > 0 && (
          <Stack space={1} direction="horizontal">
            <AccountOrderErrorMessage order={order} orderItems={orderItems} />
          </Stack>
        )}
        {orderItems && orderItems.length > 0 && categoryName && (
          <AccountOrderBodyActions
            orderNumber={order.attributes.orderNumber}
            categoryName={categoryName}
            order={order}
            orderItems={orderItems}
          />
        )}
      </Stack>
    </div>
  );
};
